import React from 'react';
import { Form, Input, Checkbox, DatePicker } from 'antd';
import { Space } from '@/features/constructorCommon/styled';
import { CHECKBOX_FIELDS } from './productFieldsConfig';
import dayjs from 'dayjs';

const { TextArea } = Input;

export const renderField = (fieldConfig, product, handlers, t) => {
  const { name, label, type, rows, min, step } = fieldConfig;
  const { onChange, onChangeNumber, onChangeDate } = handlers;
  const value = product[name];

  if (type === 'textarea') {
    return (
      <Form.Item key={name} label={t(label)}>
        <TextArea name={name} value={value} onChange={onChange} rows={rows || 3} />
      </Form.Item>
    );
  }

  if (type === 'number') {
    return (
      <Form.Item key={name} label={t(label)}>
        <Input
          name={name}
          value={value}
          type='number'
          min={min}
          step={step}
          onChange={onChangeNumber}
        />
      </Form.Item>
    );
  }

  if (type === 'date') {
    return (
      <Form.Item key={name} label={t(label)}>
        <DatePicker
          name={name}
          defaultValue={value === null ? undefined : dayjs(value)}
          onChange={onChangeDate}
          format='DD.MM.YYYY'
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item key={name} label={t(label)}>
      <Input name={name} value={value} onChange={onChange} />
    </Form.Item>
  );
};

export const renderLocalizedFields = (baseName, baseLabel, product, onChange, t, options = {}) => {
  const { type = 'input', rows } = options;

  if (!product.id) return [];

  const FieldComponent = type === 'textarea' ? TextArea : Input;
  const fieldProps = type === 'textarea' ? { rows: rows || 3 } : {};

  return [
    <Form.Item key={`${baseName}_RU`} label={`${t(baseLabel)} (RU)`}>
      <FieldComponent
        name={`${baseName}_RU`}
        value={product[`${baseName}_RU`]}
        onChange={onChange}
        {...fieldProps}
      />
    </Form.Item>,
    <Form.Item key={`${baseName}_LV`} label={`${t(baseLabel)} (LV)`}>
      <FieldComponent
        name={`${baseName}_LV`}
        value={product[`${baseName}_LV`]}
        onChange={onChange}
        {...fieldProps}
      />
    </Form.Item>
  ];
};

export const renderFieldsGroup = (
  fieldsConfig,
  product,
  handlers,
  t,
  showOnlyWhenCreated = false
) => {
  if (showOnlyWhenCreated && !product.id) return null;

  return fieldsConfig.flatMap((fieldConfig) => {
    const fields = [renderField(fieldConfig, product, handlers, t)];

    if (fieldConfig.hasLocalization && product.id) {
      const ruLabel = `${t(fieldConfig.label)} (RU)`;
      const lvLabel = `${t(fieldConfig.label)} (LV)`;

      fields.push(
        <Form.Item key={`${fieldConfig.name}_RU`} label={ruLabel}>
          <Input
            name={`${fieldConfig.name}_RU`}
            value={product[`${fieldConfig.name}_RU`]}
            onChange={handlers.onChange}
          />
        </Form.Item>,
        <Form.Item key={`${fieldConfig.name}_LV`} label={lvLabel}>
          <Input
            name={`${fieldConfig.name}_LV`}
            value={product[`${fieldConfig.name}_LV`]}
            onChange={handlers.onChange}
          />
        </Form.Item>
      );
    }

    return fields;
  });
};

export const renderCheckbox = (checkboxConfig, product, onChangeIsCheckbox, t) => {
  const { name, label } = checkboxConfig;
  return (
    <Checkbox key={name} name={name} checked={product[name]} onChange={onChangeIsCheckbox}>
      {t(label)}
    </Checkbox>
  );
};

export const renderBasicFields = (fields, product, handlers, t) => {
  return fields.flatMap((field) => {
    const renderedFields = [renderField(field, product, handlers, t)];

    if (field.hasLocalization && product.id) {
      const options = field.type === 'textarea' ? { type: 'textarea', rows: field.rows } : {};
      renderedFields.push(
        ...renderLocalizedFields(field.name, field.label, product, handlers.onChange, t, options)
      );
    }

    return renderedFields;
  });
};

export const renderCheckboxes = (product, onChangeIsCheckbox, t) => {
  const conditionalCheckboxes = CHECKBOX_FIELDS.filter((field) => !field.showAlways).map(
    (field) => (
      <React.Fragment key={field.name}>
        {renderCheckbox(field, product, onChangeIsCheckbox, t)}
        <Space />
        <Space />
      </React.Fragment>
    )
  );

  const alwaysShowCheckbox = CHECKBOX_FIELDS.find((field) => field.showAlways);

  return (
    <>
      {product.id && conditionalCheckboxes}
      {alwaysShowCheckbox && renderCheckbox(alwaysShowCheckbox, product, onChangeIsCheckbox, t)}
    </>
  );
};
